import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { auth } from '../firebase';
import useDriverAccount from '../hooks/useDriverAccount';
import FirstLoginCarModal from '../components/driver/modals/FirstLoginCarModal';

const CarContext = createContext();

export const useCarContext = () => {
    const context = useContext(CarContext);
    if (!context) {
        throw new Error('useCarContext must be used within a CarProvider');
    }
    return context;
};

export const CarProvider = ({ children }) => {
    const [cars, setCars] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [availableCars, setAvailableCars] = useState([]);
    const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
    const [hasCompletedFirstLogin, setHasCompletedFirstLogin] = useState(false);
    const { isFirstLogin: driverIsFirstLogin } = useDriverAccount();

    // Use driver's first login status, but override if user has completed the flow
    const isFirstLogin = driverIsFirstLogin && !hasCompletedFirstLogin;

    const fetchCars = async () => {
        try {
            setLoading(true);
            const idToken = await auth.currentUser?.getIdToken();

            // Use different endpoint based on first login status
            const endpoint = isFirstLogin
                ? 'https://api.servicegest.ro/driver/car/first-login'
                : 'https://api.servicegest.ro/driver/car/list';

            const response = await axios.get(endpoint, {
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json',
                }
            });

            // If first login, store available cars and show modal only if cars exist
            if (isFirstLogin) {
                setAvailableCars(response.data);

                // Only show modal if there are cars available
                if (response.data && response.data.length > 0) {
                    setShowFirstLoginModal(true);
                } else {
                    // No cars available, complete first login flow automatically
                    setHasCompletedFirstLogin(true);
                }
                return;
            }

            // Extract all appointments from all cars
            const allAppointments = [];

            const carsData = response.data.map((car, index) => {
                // Process appointments for this car
                if (Array.isArray(car.appointments)) {
                    car.appointments.forEach(appointment => {
                        allAppointments.push({
                            id: `${car.vin || index}_${appointment.appointmentStart}`,
                            service: appointment.specialization || 'Service necunoscut',
                            shopName: appointment.shopName || 'Service necunoscut',
                            date: appointment.appointmentStart,
                            status: appointment.status?.toLowerCase() || 'pending',
                            cost: null, // Not provided in API
                            carName: `${car.make} ${car.type}`,
                            carPlate: car.registrationNumber,
                            shopAddress: appointment.shopAddress, // Not provided in API
                            notes: '',
                            vin: car.vin
                        });
                    });
                }

                return {
                    ...car,
                    id: String(car.carId || car.vin || index), // Use carId from API
                    carId: String(car.carId || ''), // Keep carId separately for parking API
                    make: String(car.carDetails?.make || car.make || '-'),
                    model: String(car.carDetails?.type || car.type || '-'),
                    plate: String(car.registrationNumber || '-'),
                    year: car.carDetails?.year || '',
                    vin: car.carDetails?.vin || car.vin || '',
                    bodyType: car.carDetails?.bodyType || '',
                    engineType: car.carDetails?.engineId || '',
                    enginePower: car.carDetails?.engineCapacity || '',
                    transmissionType: '',
                    appointments: Array.isArray(car.appointments) ? car.appointments : [],
                    color: car.carDetails?.color || '-',
                    status: '-',
                    mileage: parseInt(car.carDetails?.km) || 0,
                    ITP: car.carDetails?.inspectionDue || car.inspectionDue || '',
                };
            });
            setCars(carsData);
            setAppointments(allAppointments);
        } catch (error) {
            console.error('Error fetching cars data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isFirstLogin !== undefined) {
            fetchCars();
        }
    }, [isFirstLogin]);

    const completeFirstLogin = async () => {
        // Mark first login as completed
        setHasCompletedFirstLogin(true);
        setShowFirstLoginModal(false);

        // Fetch cars using the regular endpoint
        try {
            setLoading(true);
            const idToken = await auth.currentUser?.getIdToken();
            const response = await axios.get('https://api.servicegest.ro/driver/car/list', {
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json',
                }
            });

            // Process the regular car list response
            const allAppointments = [];
            const carsData = response.data.map((car, index) => {
                // Process appointments for this car
                if (Array.isArray(car.appointments)) {
                    car.appointments.forEach(appointment => {
                        allAppointments.push({
                            id: `${car.vin || index}_${appointment.appointmentStart}`,
                            service: appointment.specialization || 'Service necunoscut',
                            shopName: appointment.shopName || 'Service necunoscut',
                            date: appointment.appointmentStart,
                            status: appointment.status?.toLowerCase() || 'pending',
                            cost: null,
                            carName: `${car.make} ${car.type}`,
                            carPlate: car.registrationNumber,
                            shopAddress: appointment.shopAddress,
                            notes: '',
                            vin: car.vin
                        });
                    });
                }

                return {
                    id: String(car.carId || car.vin || index),
                    carId: String(car.carId || ''),
                    make: String(car.carDetails?.make || car.make || '-'),
                    model: String(car.carDetails?.type || car.type || '-'),
                    plate: String(car.registrationNumber || '-'),
                    year: car.carDetails?.year || '',
                    vin: car.carDetails?.vin || car.vin || '',
                    bodyType: car.carDetails?.bodyType || '',
                    engineType: car.carDetails?.engineId || '',
                    enginePower: car.carDetails?.engineCapacity || '',
                    transmissionType: '',
                    appointments: Array.isArray(car.appointments) ? car.appointments : [],
                    color: car.carDetails?.color || '-',
                    status: '-',
                    mileage: parseInt(car.carDetails?.km) || 0,
                    ITP: car.carDetails?.inspectionDue || car.inspectionDue || '',
                };
            });

            setCars(carsData);
            setAppointments(allAppointments);
        } catch (error) {
            console.error('Error fetching cars after first login:', error);
        } finally {
            setLoading(false);
        }
    };

    const value = {
        cars,
        appointments,
        loading,
        fetchCars,
        setCars,
        setAppointments,
        availableCars,
        showFirstLoginModal,
        setShowFirstLoginModal,
        isFirstLogin,
        completeFirstLogin
    };

    return (
        <CarContext.Provider value={value}>
            {children}
            <FirstLoginCarModal />
        </CarContext.Provider>
    );
};
